import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from './supabaseClient'
import { Box, Button, TextField, Typography, CircularProgress, Paper } from '@mui/material'
import { useTranslation } from 'react-i18next'

export default function Auth() {
  const { t } = useTranslation()
  const [authMode, setAuthMode] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from || '/dashboard'

  const handleAuth = async () => {
    setLoading(true)
    setMessage('')

    try {
      let data, error

      if (authMode === 'login') {
        ;({ data, error } = await supabase.auth.signInWithPassword({ email, password }))
        if (error) throw error

        // Check if user has completed their profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', data.user.id)
          .single()

        if (!profile?.first_name || !profile?.last_name) {
          navigate('/user', { state: { requireProfile: true } })
        } else {
          navigate(from, { replace: true })
        }
      } else {
        ;({ data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              email_confirmed: false,
            },
          },
        }))
        if (error) throw error
        setMessage(t('auth.verifyEmail'))
      }
    } catch (error) {
      setMessage(error.error_description || error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setLoading(true)
    setMessage('')
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
      })
      if (error) throw error
    } catch (error) {
      setMessage(error.error_description || error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAuth()
    }
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
      <Paper elevation={3} sx={{ padding: 4, maxWidth: 400, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          {t(`auth.${authMode}`)}
        </Typography>

        {message && <Typography color="error">{message}</Typography>}

        <TextField
          fullWidth
          margin="normal"
          label={t('auth.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t('auth.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleAuth}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : t(`auth.${authMode}`)}
        </Button>

        <Typography
          variant="body2"
          sx={{ mt: 2, cursor: 'pointer' }}
          onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
        >
          {authMode === 'login' ? t('auth.switchToSignup') : t('auth.switchToLogin')}
        </Typography>

        {/* Divider or text to separate email login and social login */}
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t('auth.or')}
        </Typography>

        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={handleGoogleLogin}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : t('auth.googleLogin')}
        </Button>
      </Paper>
    </Box>
  )
}
